import React, { PropTypes, Component } from 'react';
import { connect } from 'react-redux';
import Helmet from 'react-helmet';
import uniq from 'lodash/uniq';
import { ContentStatus, SourceType, AuthTypes, Entitlements, Flags, OneIdEnv } from 'enums';
import { BAM_PAYWALL_IFRAME_ID } from 'constants';
import { singleEventPage } from 'common/page/actions';
import { fetchExternalConfig } from 'common/actions/appActions';
import {
    createCompleteCheckAuthenticationAction,
    createCompleteCheckIpAuthenticationAction,
    createDTCPaywallAction
} from 'common/actions/authenticationActions';
import { checkOneIDAuthentication } from 'common/actions/oneIdActions';
import { playbackMetaSelector, oneIdEnvSelector } from 'common/selectors/appSelector';
import { oneIdInitializedSelector } from 'common/selectors/oneIdSelector';
import { userAuthSelector } from 'common/selectors/authenticationSelectors';
import heroSelector from 'common/selectors/heroSelector';
import routingSelector from 'common/selectors/routingSelector';
import contentSubtitle from 'webapp/lib/format/contentSubtitle';
import openFeedbackPage from 'lib/ui/openFeedbackPage';
import { getDeviceTypeFromUA, isSafariBrowser } from 'lib/util';
import { authStateKnown, canPlayStream, streamHasAuthType, streamHasDTCPackage, getContentId } from 'lib/auth';
import { queryString, getMobileGameCastUrlForContent } from 'lib/format';
import { getAppLink } from 'lib/native-bridge/espnapp';
import appConfig from 'config';
import * as oneId from 'lib/oneId';
import MessagingContainer from 'webapp/modal/messaging/MessagingContainer';

const { OneIdJs } = oneId;

const {
    shellVersion,
    secureCdnHost,
    devStyles
} = appConfig;

let STYLE_HREF = `${secureCdnHost}/redesign/${shellVersion}/css`;
if (devStyles) {
    STYLE_HREF = 'http://localhost:8080/static/css';
}

const appStore = {
    ios: {
        link: 'https://itunes.apple.com/us/app/espn-live-sports-scores/id317469184?mt=8',
        iconHref: `${secureCdnHost}/watchespn/images/roadblock/app_store_button.png`
    },
    android: {
        link: 'https://play.google.com/store/apps/details?id=com.espn.score_center&hl=en',
        iconHref: `${secureCdnHost}/watchespn/images/roadblock/android_app_store_button.png`
    }
};

const classes = {
    container: 'watch__roadblock',
    logo: 'watch__roadblock-logo',
    pageTitle: 'watch__roadblock_page-title',
    roadblockText: 'watch__roadblock-text',
    mainFeature: 'watch__roadblock-main-feature',
    inner: 'watch__roadblock-inner',
    eventName: 'watch__roadblock-event-name',
    eventTitle: 'watch__roadblock-event-title',
    eventMeta: 'watch__roadblock-event-meta',
    main: 'watch__roadblock-main',
    fullSite: 'watch__roadblock-full-site',
    footer: 'watch__roadblock-footer',
    footerA: 'watch__roadblock-footer-a',
    submitButton: 'watch__roadblock-submit-btn',
    windowsphone: 'watch__roadblock-windowsphone'
};

class Roadblock extends Component {
    constructor(props) {
        super(props);
        this.state = {
            initialModal: false
        };
        this.handleLaunchClick = this.handleLaunchClick.bind(this);
    }

    componentWillMount() {
        this.setState({
            deviceType: getDeviceTypeFromUA(),
            isSafari: isSafariBrowser()
        });
    }

    async componentDidMount() {
        const { dispatch } = this.props;
        await dispatch(checkOneIDAuthentication());
    }

    componentWillReceiveProps(nextProps) {
        const { initialModal } = this.state;
        if (!initialModal) {
            const actionTriggered = this.createDTCPaywallAction(nextProps);
            if (actionTriggered) {
                this.setState({ initialModal: true });
            }
        }
    }

    createDTCPaywallAction(props = this.props) {
        const { dispatch, hero, oneIdInitialized, userAuth } = props;
        const { streams = [] } = hero;
        if (oneIdInitialized && (authStateKnown(userAuth) || !userAuth.length)) {
            const requiredAuths = uniq(streams.reduce((acc, { authTypes }) => (acc.concat(authTypes)), []));
            if (requiredAuths.every(authType => authType === AuthTypes.BAM)) { // DTC-only content
                const stream = streams[0];
                if (!canPlayStream(stream, userAuth, false)) {
                    dispatch(createDTCPaywallAction({
                        containerId: BAM_PAYWALL_IFRAME_ID,
                        content: hero,
                        stream
                    }));
                    return true;
                }
            }
        }
        return false;
    }

    handleLaunchClick() {
        const actionTriggered = this.createDTCPaywallAction();
        if (actionTriggered) {
            return;
        }

        const { hero, routing: { locationBeforeTransitions } } = this.props;
        const { query = {} } = locationBeforeTransitions || {};
        const { imageHref, name, streams = [], status } = hero;
        const contentId = getContentId(hero);
        const { source: { id: sourceId, type: sourceType, name: sourceName } = {} } = streams[0] || {};
        const { isSafari } = this.state;
        let isBranchLinkInParams = false;
        let updateAppLink = false;
        let { deviceType } = this.state;
        let params = {
            imageHref,
            name,
            network: sourceName
        };
        if (query.deviceType) {
            deviceType = query.deviceType;
        }
        // See: https://espnjira.disney.com/browse/WEWEB-2071
        if (query._branch_match_id && query._branch_match_id !== '') {
            isBranchLinkInParams = true;
        }
        // See: https://espnjira.disney.com/browse/WEWEB-2500
        if (deviceType === 'android' && query['om-navmethod'] && query['om-navmethod'] === 'espn:ufc:gamehq') {
            updateAppLink = true;
        }

        // https://confluence.espn.com/pages/viewpage.action?title=ESPN+App+Deep+Linking&spaceKey=CD#ESPNAppDeepLinking-PlayWatchESPNVideos(LiveandReplay)
        if (sourceId && status !== ContentStatus.REPLAY && sourceType === SourceType.LINEAR) {
            // linear content (that is not instant turnaround) - play by channel so stale content doesn't error
            params = {
                ...params,
                playChannel: sourceId.toLowerCase()
            };
        } else if (contentId.eventId) {
            params = {
                ...params,
                playGameID: contentId.eventId
            };
        } else {
            // replays - play by cmsId
            params = {
                ...params,
                playID: contentId.id
            };
        }

        const isIosDevice = (typeof deviceType === 'string' && deviceType === 'ios');
        const shouldUseProtocolOnly = (isIosDevice && isSafari && isBranchLinkInParams);
        let url = getAppLink('showWatchStream', params, shouldUseProtocolOnly);
        if (updateAppLink) {
            url = `android-app://com.espn.score_center/${url}`;
        }

        window.location = url;
    }

    renderRoadblockLinks() {
        let { deviceType } = this.state;
        const { routing: { locationBeforeTransitions } } = this.props;
        const { query = {} } = locationBeforeTransitions || {};

        if (query.deviceType) { // override default detection
            deviceType = query.deviceType;
        }

        if (!deviceType) {
            return null;
        }

        const storeProps = appStore[deviceType];
        return storeProps ? (
            <div>
                <span className={`strong ${classes.roadblockText}`}>Download the ESPN App</span>
                <div>
                    <p className={deviceType === 'windowsphone' ? classes.windowsphone : ''}>
                        <a className="storeLink" href={storeProps.link}>
                            <img width="150" src={storeProps.iconHref} alt="App Store Badge" />
                        </a>
                    </p>
                </div>
                <span className={classes.roadblockText}>Already have the ESPN App?</span>
                <p>
                    <a className={`blue ${classes.submitButton}`} onClick={this.handleLaunchClick}>Launch App</a>
                </p>
            </div>
        ) : <span className={classes.roadblockText}>The ESPN App is not available on your device.</span>;
    }

    render() {
        const {
            hero,
            playbackMeta: { tracking } = {},
            routing: { locationBeforeTransitions },
            oneIdEnv: oneIdEnvArg
        } = this.props;
        const { query = {} } = locationBeforeTransitions || {};
        const { imageHref, name, streams = [] } = hero;
        const stream = streams[0] || {};
        const formattedSubtitle = contentSubtitle(hero);
        const qs = queryString({
            ...query,
            [Flags.FROM_ROADBLOCK]: true
        });
        const fullSiteLink = `/watch${qs}`;
        const mobileGameCastUrl = getMobileGameCastUrlForContent(hero, tracking);
        let oneIdEnv = oneIdEnvArg;
        if (query[Flags.DISNEY_ENV] === 'stg') {
            oneIdEnv = OneIdEnv.STAGING;
        }

        let logo = <img alt="ESPN" width="200" src={`${secureCdnHost}/watchespn/images/roadblock/espn@2x.png`} />;
        if (streamHasAuthType(stream, AuthTypes.BAM)) {
            if (streamHasDTCPackage(stream, Entitlements.ESPN_PLUS_MLB)) {
                logo = (
                    <img
                        alt="MLB.TV"
                        width="250"
                        height="18"
                        src={`${secureCdnHost}/watchespn/images/roadblock/mlb@2x.png`}
                    />
                );
            } else if (streamHasDTCPackage(stream, Entitlements.ESPN_PLUS_NHL)) {
                logo = (
                    <img
                        alt="NHL.TV"
                        width="250"
                        src={`${secureCdnHost}/watchespn/images/roadblock/nhl@2x.png`}
                    />
                );
            } else {
                logo = (
                    <img
                        alt="ESPN+"
                        width="200"
                        src={`${secureCdnHost}/watchespn/images/roadblock/espn-plus@2x.png`}
                    />
                );
            }
        }

        return (
            <div className={classes.container}>
                <Helmet
                    meta={[{
                        name: 'viewport',
                        content: 'width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no'
                    }]}
                    link={[
                        { rel: 'stylesheet', href: `${STYLE_HREF}/page-shell.css` },
                        { rel: 'stylesheet', href: `${STYLE_HREF}/watch-provider.css` }
                    ]}
                    style={[{
                        cssText: `
                            body {
                                margin: 0;
                                background: #fff !important;
                                font: normal 12px/18px BentonSans, helvetica, arial, sans-serif;
                                min-width: 100%;
                            }
                            #${BAM_PAYWALL_IFRAME_ID} iframe {
                                width: 100%;
                                height: 100%;
                                border: none;
                            }
                            .${classes.eventName} {
                                padding-left: 5px;
                            }
                            .${classes.logo} {
                                text-align: center;
                            }
                            .${classes.logo} img { margin: 20px auto; }
                            .${classes.pageTitle} {
                                margin-bottom: 20px;
                            }
                            .${classes.roadblockText} {
                                display: block;
                                color: #262626 !important;
                                font-size: 16px;
                            }
                            .${classes.container} {
                                width: 295px !important;
                                background: #fff !important;
                                margin: 0 auto;
                                border-radius: 12px;
                                text-align: center;
                                -moz-border-radius: 0 !important;
                                -webkit-border-radius: 0 !important;
                                box-shadow: none !important;
                                -webkit-box-shadow: none !important;
                            }
                            .${classes.submitButton} {
                                display: block;
                                position: relative;
                                width: 80% !important;
                                margin: 0 auto;
                                background: #2d66eb;
                                border: 1px solid white;
                                border-radius: 40px;
                                box-shadow: none;
                                line-height: 45px;
                                color: #eee;
                                cursor: pointer;
                                text-align: center;
                                text-transform: none;
                                text-shadow: none;
                                text-decoration: none;
                                font-family: BentonSansMedium, helvetica, sans-serif;
                                font-size: 18px;
                                font-weight: 600;
                                height: 45px;
                            }

                            .${classes.submitButton}.blue {
                                border: 1px solid #2D66EB !important;
                                background: #fff;
                                color: #2D66EB;
                            }
                            .${classes.submitButton}:hover {text-decoration: none !important;}

                            .${classes.main} a {
                                cursor: pointer;
                            }
                            .${classes.main} a:hover {text-decoration: underline;}

                            .${classes.main} p {
                                text-align: center;
                                margin: 10px 0 25px 0;
                            }
                            .${classes.main} .${classes.fullSite} {
                                color: #2d66eb;
                                font-size: 14px;
                                font-family: BentonSans, helvetica, arial, sans-serif;
                                text-decoration: none;
                            }
                            .${classes.footer} {
                                display: flex;
                                justify-content: space-between;
                            }

                            .${classes.footer} a {
                                color: #0066CC !important;
                                flex-grow: 1;
                                padding: 4px 0;
                            }

                            .${classes.footer} a:first-child{
                                font-weight: 600;
                            }

                            .${classes.footer} a + a{
                                border-left: 1px #dcdddf solid;
                            }

                            .${classes.mainFeature} {
                                position: relative;
                                overflow: visible;
                                width: 100%;
                                left: 0;
                                margin-bottom: 30px;
                            }

                            .${classes.mainFeature} figure {
                                border: none !important;
                            }

                            .${classes.mainFeature} figure img{
                                max-width: 100%;
                                border-radius: 10px;
                                box-shadow: 0 4px 24px rgba(0,0,0,0.16);
                                margin-bottom: 12px;
                                overflow: hidden;
                            }

                            .${classes.mainFeature} figure figcaption {
                                bottom: auto;
                                display: block;
                                font-size: 12px;
                                left: auto;
                                line-height: 1.2;
                                position: relative;
                                text-align: left;
                            }

                            .${classes.eventTitle} {
                                color: #151617;
                                font-weight: 600;
                            }
                            .${classes.eventMeta} {
                                border-bottom: 1px #DCDDDF dotted;
                                color: #a5a6a7;
                                margin-top: 4px;
                                padding-bottom: 12px;
                            }
                            .${classes.eventMeta} span + span{
                                margin-left: 4px;
                            }
                            .${classes.eventMeta} span + span:before{
                                color: #cbccce;
                                content: '•';
                                margin-right: 4px;
                            }


                            .${classes.inner} {
                                width: 100%;
                                display: table;
                                text-align: left;
                                background: rgba(0, 0, 0, 0.6) no-repeat;
                                min-height: 25px;
                            }
                            .${classes.inner} span {
                                display: table-cell;
                                vertical-align: middle;
                                margin-left: 10px;
                                padding: 10px;
                                font-size: 1.2em;
                                line-height: 1.2em;
                                color: #fff;
                            }
                            .${classes.windowsphone} {
                                width: 200px;
                                height: 63px;
                                margin: 10px auto 25px auto !important;
                                padding-top: 10px;
                                border: solid 1pt #515151;
                                background: #000;
                            }

                            .strong{
                                font-weight: 600;
                            }

                            .lightbox-container .lightbox-watch.watch-provider-modal.lightbox-paywall .btn-close {
                                padding: 0;
                            }
                            .lightbox-container .lightbox-watch.watch-provider-modal.lightbox-paywall .btn-close:before {
                                color: #fff;
                                line-height: 36px;
                            }
                        `
                    }]}
                    script={[{
                        src: OneIdJs[oneIdEnv]
                    }]}
                />
                <div className={classes.logo}>
                    {logo}
                </div>

                <header className={`strong ${classes.roadblockText} ${classes.pageTitle}`}>
                    To stream this event on your phone,<br />you need the ESPN App.
                </header>

                <section className={classes.mainFeature}>
                    <figure>
                        <img src={imageHref} alt={name} />
                        <figcaption>
                            <div className={classes.eventTitle}>{name}</div>
                            <div className={classes.eventMeta}>
                                <span>{formattedSubtitle}</span>
                            </div>
                        </figcaption>
                    </figure>
                </section>

                <section className={`section ${classes.main}`}>
                    {this.renderRoadblockLinks()}
                    {mobileGameCastUrl ? (
                        <p>
                            <a className={classes.fullSite} href={mobileGameCastUrl}>
                                Follow on GameCast &raquo;
                            </a>
                        </p>
                    ) : null}
                    <p style={{ display: 'none' }}>
                        <a className={classes.fullSite} href={fullSiteLink}>
                            View Full Site
                        </a>
                    </p>
                </section>

                <div className={classes.footer}>
                    <a href="http://www.espn.com">ESPN.com</a>
                    <a className={classes.footerA} href="/watch/schedule">Schedule</a>
                    <a
                        className={classes.footerA}
                        href="//support.espn.com/hc/en-us/categories/115000435812-Streaming-on-ESPN" rel="nofollow"
                    >FAQs</a>
                    <a className={classes.footerA} onClick={openFeedbackPage}>Customer Care</a>
                </div>

                <MessagingContainer />
            </div>
        );
    }
}

Roadblock.propTypes = {
    dispatch: PropTypes.func,
    hero: PropTypes.object,
    playbackMeta: PropTypes.object,
    routing: PropTypes.object,
    oneIdEnv: PropTypes.string
};


Roadblock.loadActions = [
    fetchExternalConfig,
    (pathParams, query) => (dispatch) => {
        const id = 'roadblock';
        const name = 'Roadblock';
        dispatch(createCompleteCheckIpAuthenticationAction(false));
        dispatch(createCompleteCheckAuthenticationAction(false));
        return dispatch(singleEventPage(id, name, query));
    }
];

const mapStateToProps = (state) => ({
    hero: heroSelector(state),
    playbackMeta: playbackMetaSelector(state),
    routing: routingSelector(state),
    userAuth: userAuthSelector(state),
    oneIdEnv: oneIdEnvSelector(state),
    oneIdInitialized: oneIdInitializedSelector(state)
});

export default connect(mapStateToProps)(Roadblock);



// WEBPACK FOOTER //
// ./app/webapp/page/Roadblock.js